import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("login"); // login | signup
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);

  const gotoByRole = (u) => {
    if (u.role === "admin" || u.role === "staff") nav("/dashboard", { replace: true });
    else nav("/customer", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name}`);
        gotoByRole(u);
      } else {
        if (!form.name || !form.email || !form.password) {
          toast.error("Fill name, email and password");
          setBusy(false); return;
        }
        const u = await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
        toast.success("Account created");
        gotoByRole(u);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

const googleLogin = () => {
    window.location.href =
        "/api/auth/google";
};
  return (
    <div className="min-h-screen relative flex items-stretch">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0e0e0e] via-[#0a0a0a] to-black" />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `
              radial-gradient(circle at 30% 20%, rgba(251,206,24,0.14), transparent 40%),
              radial-gradient(circle at 80% 80%, rgba(251,206,24,0.06), transparent 40%)`,
          }}
        />
        <div className="relative z-10 p-14 flex flex-col justify-between w-full">
          <Link to="/" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2" data-testid="login-back-home">
            <ArrowLeft size={16} /> Back home
          </Link>
<div>
<div className="text-[11px] tracking-[0.28em] uppercase text-[#fbce18]">
  CUSTOMER PORTAL
</div>

<h1 className="mt-4 font-display text-4xl xl:text-5xl tracking-tight leading-[1.05]">
  Welcome to<br/>
  <span className="text-[#fbce18]">LostDRKC</span> Payments.
</h1>

<p className="mt-4 text-white/50 max-w-md leading-7">
  Securely manage your purchases, subscriptions, invoices and payment methods across all LostDRKC services.
</p>

            <div className="mt-6"><Logo size={52} /></div>
          </div>

<div
  className="text-xs tracking-[0.28em] uppercase"
  style={{ color: "rgba(255,255,255,0.4)" }}
>
  © 2024-{new Date().getFullYear()} LOSTDRKC. All rights reserved.
</div>

        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md ldc-glass p-8" data-testid="login-card">
          <Logo size={52} />
          <p className="text-white/50 text-sm mt-3">Sign in to access your console.</p>

          <div className="grid grid-cols-2 gap-1 mt-6 p-1 rounded-md bg-white/5 border border-white/8">
            <button
              type="button" onClick={() => setTab("login")}
              data-testid="login-tab-signin"
              className={`text-sm py-2 rounded transition-colors ${tab === "login" ? "bg-[#fbce18] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              Sign in
            </button>
            <button
              type="button" onClick={() => setTab("signup")}
              data-testid="login-tab-signup"
              className={`text-sm py-2 rounded transition-colors ${tab === "signup" ? "bg-[#fbce18] text-black font-semibold" : "text-white/60 hover:text-white"}`}>
              Create account
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-3">
            {tab === "signup" && (
              <>
                <Field label="Name" name="name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="login-input-name" />
                <Field label="Phone" name="phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="login-input-phone" />
              </>
            )}
            <Field label="Email" name="email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="login-input-email" />
            <Field label="Password" name="password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="login-input-password" />

            <button
              type="submit"
              disabled={busy}
              className="ldc-btn-primary w-full py-3 rounded-md mt-2 inline-flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {tab === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="flex items-center gap-4 my-5">
            <div className="h-px flex-1 bg-white/8" />
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">OR</div>
            <div className="h-px flex-1 bg-white/8" />
          </div>

          <button
            onClick={googleLogin}
            data-testid="login-google-btn"
            className="w-full py-3 rounded-md border border-white/10 hover:border-[#fbce18]/40 transition-colors text-sm inline-flex items-center justify-center gap-2 bg-white/[0.03]"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <p className="mt-6 text-xs text-white/40 text-center">
            By continuing you agree to LostDRKC Payments' terms.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="ldc-input w-full rounded-md px-3 py-2.5 text-sm"
        data-testid={testid}
      />
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.5 32.6 29.1 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.3l-6.3-5.3C29.2 34.9 26.7 36 24 36c-5.1 0-9.4-3.3-11.1-7.8l-6.6 5.1C9.5 39.5 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.3 5.3C41.1 34.8 44 29.8 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
